import {
  dataAttribute,
  DataAttributes,
  IColumn,
  RESET_CELLS_PADDING_CLASSNAME,
  Stack,
  Text
} from "@bps/fluent-ui";
import { ClaimStatuses } from "@libs/gateways/acc/AccGateway.dtos.ts";
import {
  ContactResultItemDto,
  ContactType,
  OrganisationContactDto
} from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { ClaimAdjustmentCallout } from "@modules/acc/screens/claim-adjustment/components/ClaimAdjustmentCallout.tsx";
import { isClaimAdjustmentAllowed } from "@modules/acc/screens/claim-adjustment/components/utils.ts";
import { ClaimStatusBadge } from "@modules/acc/screens/shared-components/claim-status-badge/ClaimStatusBadge.tsx";
import { filterInsurers } from "@modules/practice/screens/shared-components/utils/contact.utils.ts";
import { AccStore } from "@stores/acc/AccStore.ts";
import { Claim } from "@stores/acc/models/Claim.ts";
import { Item } from "@ui-components/filter-bar/FilterBar.types.ts";
import { Navigate } from "@ui-components/navigation/Navigate.tsx";

import { ClaimNumberCell } from "../ClaimNumberCell.tsx";
import { ClaimsContextualMenu } from "../ClaimsContextualMenu.tsx";
import { ClaimsListInjuryDiagnosisText } from "../ClaimsListInjuryDiagnosisText.tsx";
import { ClaimsFilterState } from "./claims-filter.types.ts";

export const getClaimStatusesOptions = (acc: AccStore) => {
  const statusesRefData = acc.ref.claimStatuses;

  const filteredOptions = statusesRefData.keyTextValues.filter(
    x =>
      x.key !== ClaimStatuses.Ready &&
      x.key !== ClaimStatuses.Error &&
      x.key !== ClaimStatuses.GetStatusError
  );

  const options = [
    {
      key: ClaimStatuses.Ready,
      text: statusesRefData.get(ClaimStatuses.Ready)?.text ?? "Ready",
      isDivider: true
    },
    ...filteredOptions
      .sort((a, b) => a.text.localeCompare(b.text))
      .map((x, i) => {
        if (i + 1 === filteredOptions.length) {
          return { ...x, isDivider: true };
        }
        return x;
      }),

    {
      key: ClaimStatuses.Error,
      text: statusesRefData.get(ClaimStatuses.Error)?.text ?? "Lodge error"
    },
    {
      key: ClaimStatuses.GetStatusError,
      text:
        statusesRefData.get(ClaimStatuses.GetStatusError)?.text ?? "Query error"
    }
  ];

  return options;
};

export const claimsFilterItems = (
  patientLabel: string,
  acc: AccStore
): Item<ClaimsFilterState>[] => [
  {
    type: "contactPicker",
    name: "patients",
    stickItem: true,
    props: {
      styles: { root: { width: 400, minWidth: 400 } },
      inputProps: { placeholder: `Search for ${patientLabel}` }
    }
  },
  {
    name: "claimNumber",
    type: "searchBox",
    stickItem: true,
    props: {
      placeholder: "Search by claim number",
      styles: { root: { maxWidth: 250, minWidth: 250 } }
    }
  },
  {
    type: "bookableUsersSelect",
    name: "providers",
    props: {
      id: "claim-filter-providers",
      multiSelect: true,
      placeholder: "Provider",
      calloutWidth: 250
    }
  },
  {
    type: "contactsSelect",
    name: "insurers",
    props: {
      id: "claim-filter-insurers",
      multiSelect: true,
      placeholder: "Insurer",
      calloutWidth: 250,
      contactFilter: { types: [ContactType.Organisation] },
      resultFilterPredicate: (contact: ContactResultItemDto) => {
        const organisation = contact.value as OrganisationContactDto;
        return filterInsurers(organisation, false);
      }
    }
  },
  {
    type: "optionsSelect",
    name: "claimStatusCode",
    props: {
      id: "claim-filter-status",
      placeholder: "Status",
      options: getClaimStatusesOptions(acc),
      multiSelect: true,
      hideSearchOption: true,
      calloutWidth: 200
    }
  },
  {
    type: "datesRangePicker",
    name: "datesRangePicker",
    props: {
      id: "claims-filter-dates-range",
      startDateProps: { id: "claims-filter-start-date" },
      endDateProps: { id: "claims-filter-end-date" },
      startDateName: "dateFrom",
      endDateName: "dateTo",
      placeholder: "Date(s)"
    }
  }
];

export const claimsListColumns = ({
  searchString,
  patientLabel
}: {
  searchString: string;
  patientLabel: string;
}): IColumn[] => [
  {
    name: "Actions",
    className: RESET_CELLS_PADDING_CLASSNAME,
    key: "action",
    minWidth: 50,
    maxWidth: 50,
    onRender: (claim: Claim) => <ClaimsContextualMenu claim={claim} />
  },
  {
    name: "Status",
    key: "claimStatus",
    className: RESET_CELLS_PADDING_CLASSNAME,
    minWidth: 120,
    maxWidth: 120,
    onRender: (claim: Claim) => (
      <ClaimStatusBadge
        styles={{
          root: {
            horizontalAlign: "start",
            styles: { root: { marginLeft: 12 } }
          }
        }}
        status={claim.claimStatus}
        isDischarged={claim.isDischarged}
      />
    )
  },
  {
    name: "Claim",
    key: "claimNumber",
    minWidth: 80,
    maxWidth: 100,
    onRender: (claim: Claim) => (
      <ClaimNumberCell claim={claim} filter={searchString} />
    )
  },
  {
    name: patientLabel,
    key: "name",
    minWidth: 100,
    maxWidth: 200,
    onRender: (claim: Claim) =>
      claim.patientId && (
        <Navigate
          {...dataAttribute(DataAttributes.Element, "patient-Name")}
          to={routes.contacts.contact.path({
            id: claim.patientId
          })}
        >
          {claim.patientFullName}
        </Navigate>
      )
  },
  {
    name: "Diagnosis",
    key: "injuryDiagnosis",
    minWidth: 100,
    maxWidth: 150,
    onRender: (claim: Claim) => (
      <Stack>
        <Text nowrap>
          {!!claim.currentDiagnoses && claim.currentDiagnoses.length > 0 && (
            <ClaimsListInjuryDiagnosisText diagnosis={claim.currentDiagnoses} />
          )}
        </Text>
      </Stack>
    )
  },
  {
    name: "Provider",
    key: "provider",
    minWidth: 100,
    maxWidth: 200,
    onRender: (claim: Claim) =>
      claim.providerFullName && <Text>{claim.providerFullName}</Text>
  },
  {
    name: "Initial consult",
    key: "initialConsultDate",
    minWidth: 80,
    maxWidth: 100,
    onRender: (claim: Claim) =>
      !!claim.initialConsultDate &&
      claim.initialConsultDate.toDayDefaultFormat()
  },
  {
    name: "ACC32",
    key: "claimAdjustments",
    minWidth: 120,
    maxWidth: 120,
    onRender: (claim: Claim) => {
      if (
        claim.claimAdjustments.length > 1 &&
        isClaimAdjustmentAllowed(claim.claimStatus ?? "")
      ) {
        return <ClaimAdjustmentCallout claim={claim} />;
      } else if (claim.claimAdjustments?.length) {
        return (
          <Navigate
            to={routes.claimAdjustment.edit.path({
              id: claim.claimAdjustments[0].id,
              claimId: claim.id
            })}
          >
            {claim.claimAdjustments[0].formattedNumber}
          </Navigate>
        );
      }
      return;
    }
  },
  {
    name: "Insurer",
    key: "insurerName",
    minWidth: 100,
    maxWidth: 200,
    onRender: (claim: Claim) =>
      claim.insurerName && <Text>{claim.insurerName}</Text>
  }
];
